import { Controller } from "@hotwired/stimulus"
import flatpickr      from "flatpickr"
import { Portuguese } from "flatpickr/dist/l10n/pt.js"
import { english }    from "flatpickr/dist/l10n/default.js"
import { Spanish }    from "flatpickr/dist/l10n/es.js"
import { maskFormatters } from "./mask_controller.js"

// Date picker — wrapper sobre Flatpickr.
//
// Aplicado automaticamente pelo partial `_date_picker.html.erb`. Substitui o
// <input type="date"> nativo (limitado e visualmente inconsistente entre
// browsers) por um popup customizado totalmente alinhado ao shadcn.
//
// Modos suportados:
//   - single (default)         → escolhe uma data
//   - range (mode: "range")    → intervalo (data início + fim)
//   - time (enable_time: true) → adiciona seletor de hora
//
// === LOCALES ===
// Auto-detectado por `<html lang="...">` (preenchido pelo Rails via I18n.locale).
// Flatpickr traz ~50 locales prontos em `node_modules/flatpickr/dist/l10n/`.
//
// === FORMATO DA DATA (CRÍTICO) ===
// Usamos `altInput: true`: Flatpickr cria DOIS inputs:
//   1. Hidden (o real, enviado no form) → SEMPRE ISO `Y-m-d`
//   2. Visível pro usuário              → formato do locale (`d/m/Y` em pt-BR)
//
// Resultado: o backend SEMPRE recebe ISO, independente do idioma da UI.

const LOCALES = {
  "pt-BR": Portuguese,
  "pt":    Portuguese,
  "en":    english,
  "es":    Spanish
}

// Formato visível por locale. Default é d/m/Y (formato BR/EU).
const VISIBLE_DATE_FORMAT = {
  "en":    "m/d/Y",
  "pt-BR": "d/m/Y",
  "pt":    "d/m/Y",
  "es":    "d/m/Y"
}

// Formato visível de hora. EN usa 12h (h:i K → "02:30 PM"); resto usa 24h (H:i → "14:30").
const VISIBLE_TIME_FORMAT = {
  "en":    "h:i K",
  "pt-BR": "H:i",
  "pt":    "H:i",
  "es":    "H:i"
}

export default class extends Controller {
  static values = {
    mode:        { type: String,  default: "single" },
    enableTime:  { type: Boolean, default: false },
    timeOnly:    { type: Boolean, default: false },
    minDate:     { type: String,  default: "" },
    maxDate:     { type: String,  default: "" },
    dateFormat:  { type: String,  default: "" }
  }
  static targets = ["input"]

  connect() {
    const lang   = document.documentElement.lang || "pt-BR"
    const locale = LOCALES[lang] || Portuguese
    // time_only força enableTime e usa só formato de hora (H:i — 24h).
    const isTimeOnly = this.timeOnlyValue
    const enableTime = isTimeOnly || this.enableTimeValue

    const dateFmt = VISIBLE_DATE_FORMAT[lang] || "d/m/Y"
    const timeFmt = VISIBLE_TIME_FORMAT[lang] || "H:i"

    const visibleFmt = this.dateFormatValue || (
      isTimeOnly                ? timeFmt :
      enableTime                ? `${dateFmt} ${timeFmt}` :
                                  dateFmt
    )

    // altInput: true mantém DOIS inputs separados (visível formatado + hidden ISO).
    // Backend SEMPRE recebe ISO (`Y-m-d` ou `Y-m-d H:i` ou `H:i`) — sem depender
    // de locale do user. mask_controller (no mesmo elemento) formata o input
    // visível enquanto user digita; Flatpickr sincroniza valor → hidden ISO.
    this.fp = flatpickr(this.inputTarget, {
      locale,
      dateFormat:          isTimeOnly ? "H:i" : (enableTime ? "Y-m-d H:i" : "Y-m-d"),
      altInput:            true,
      altFormat:           visibleFmt,
      mode:                this.modeValue,
      enableTime,
      noCalendar:          isTimeOnly,
      time_24hr:           lang !== "en",
      minDate:             this.minDateValue || null,
      maxDate:             this.maxDateValue || null,
      allowInput:          true,
      clickOpens:          false,    // calendário só abre via botão (libera input pra digitar)
      animate:             true,
      position:            "auto",
      showMonths:          1,
      monthSelectorType:   "static"
    })

    // Aplica máscara no input VISÍVEL (altInput) — é onde o user digita.
    // O input original (hidden) recebe valor ISO de forma transparente.
    if (this.fp.altInput) {
      this.fp.altInput.className = this.inputTarget.className
      this.fp.altInput.setAttribute("inputmode", "numeric")
      if (this.inputTarget.placeholder) {
        this.fp.altInput.setAttribute("placeholder", this.inputTarget.placeholder)
      }

      // Máscara de digitação só funciona em locales 24h (pt-BR/es). Em EN o
      // formato 12h requer "AM"/"PM" no fim — Flatpickr cuida disso direto.
      const maskType = this.inputTarget.dataset.maskTypeValue
      const supportsMask = maskType && lang !== "en"
      if (supportsMask) {
        // Wire mask manualmente: aplica formatação a cada keystroke.
        // Não usa Stimulus aqui porque o input é criado em runtime pelo Flatpickr.
        const apply = () => {
          this.fp.altInput.value = maskFormatters[maskType](
            this.fp.altInput.value.replace(/\D/g, "")
          )
        }
        this.fp.altInput.addEventListener("input", apply)
        apply()  // formato inicial
      }
    }

    // Turbo cache: o snapshot da página é gravado ANTES do disconnect. Como o
    // flatpickr injeta o `.flatpickr-calendar` (e o altInput) no fluxo do
    // documento, o snapshot os captura; ao voltar (restore), o Turbo repinta o
    // DOM cacheado E o connect roda de novo criando OUTRO calendário — a cada
    // ida-e-volta acumula mais um. Desfazemos o flatpickr em `turbo:before-cache`
    // (dispara antes do snapshot) para o cache guardar só o <input> original.
    this._beforeCache = () => { this.fp?.destroy(); this.fp = null }
    document.addEventListener("turbo:before-cache", this._beforeCache)

    // Valor setado por OUTRO controller (ex.: reference-month-suggestion escrevendo
    // a competência sugerida): como o altInput mantém o texto visível separado do
    // input ISO, escrever `.value` sozinho não repinta nada. Quem escreve emite
    // `date-picker:sync` e nós resincronizamos o Flatpickr a partir do input.
    this._sync = () => this.fp?.setDate(this.inputTarget.value, false)
    this.inputTarget.addEventListener("date-picker:sync", this._sync)
  }

  // Acionado pelo <button data-action="date-picker#open"> dentro do wrapper.
  open() {
    if (!this.fp) return
    this.fp.isOpen ? this.fp.close() : this.fp.open()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._beforeCache)
    this.inputTarget.removeEventListener("date-picker:sync", this._sync)
    this.fp?.destroy()
    this.fp = null
  }
}
